import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Bot } from "lucide-react";
import { useTranslation } from "react-i18next";
import { sendChat } from "../lib/api";

function uid() {
  return "sess-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function AIChatWidget() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: t("chat.welcome"),
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);

  // When language changes and chat hasn't been used yet, refresh welcome.
  useEffect(() => {
    setMessages((m) => {
      if (m.length === 1 && m[0].role === "assistant") {
        return [{ role: "assistant", text: t("chat.welcome") }];
      }
      return m;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    try {
      let id = localStorage.getItem("nibro_chat_session");
      if (!id) {
        id = uid();
        localStorage.setItem("nibro_chat_session", id);
      }
      setSessionId(id);
    } catch {
      setSessionId(uid());
    }
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const toggle = () => setOpen((p) => !p);

  const submit = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const res = await sendChat(sessionId || uid(), msg);
      setMessages((m) => [...m, { role: "assistant", text: res.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: t("chat.error") },
      ]);
    } finally { setLoading(false); }
  };

  return (
    <>
      <div
        className={`fixed bottom-24 right-4 z-[60] flex h-[600px] max-h-[80vh] w-[400px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-lg border border-nibro-sage bg-white shadow-2xl transition-all duration-300 ${
          open ? "pointer-events-auto translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"
        }`}
        data-testid="ai-chat-panel"
        aria-hidden={!open}
      >
        <div className="flex items-center justify-between border-b border-nibro-sage/30 px-4 py-3" style={{ backgroundColor: "var(--nibro-sage)" }}>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white/25">
              <Bot size={16} className="text-white" />
            </div>
            <div>
              <div className="font-display text-sm font-bold text-white">
                {t("chat.title")}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-white/80">
                {t("chat.status")}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-md p-1.5 text-white/90 hover:bg-white/20"
            data-testid="ai-chat-close"
            aria-label={t("chat.close_aria")}
          >
            <X size={16} />
          </button>
        </div>

        <div
          ref={listRef}
          className="flex-1 space-y-3 overflow-y-auto bg-zinc-50 p-4"
          data-testid="ai-chat-messages"
        >
          {messages.map((m, i) => (
            <div
              key={i}
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                m.role === "user"
                  ? "ml-auto bg-nibro-sage text-white"
                  : "border border-zinc-200 bg-white text-zinc-800"
              }`}
              data-testid={`ai-chat-message-${m.role}`}
            >
              <div className="whitespace-pre-wrap">{m.text}</div>
            </div>
          ))}
          {loading && (
            <div className="max-w-[85%] rounded-2xl border border-zinc-200 bg-white px-4 py-2.5 text-sm text-zinc-500">
              <span className="inline-flex gap-1">
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-nibro-sage [animation-delay:-0.3s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-nibro-sage [animation-delay:-0.15s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-nibro-sage" />
              </span>
            </div>
          )}
        </div>

        <div className="border-t border-zinc-200 bg-white p-3">
          <div className="flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder={t("chat.placeholder")}
              className="flex-1 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-nibro-sage focus:outline-none"
              data-testid="ai-chat-input"
            />
            <button
              type="button"
              onClick={submit}
              disabled={!input.trim() || loading}
              className="flex h-9 w-9 items-center justify-center rounded-md bg-nibro-sage text-white transition hover:bg-nibro-sage-hover disabled:opacity-50"
              data-testid="ai-chat-send"
              aria-label={t("chat.send_aria")}
            >
              <Send size={14} />
            </button>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={toggle}
        className="fixed bottom-4 right-4 z-[60] flex h-12 w-12 items-center justify-center rounded-full text-white shadow-xl transition hover:scale-105 sm:bottom-6 sm:right-6 sm:z-[100] sm:h-14 sm:w-14"
        style={{ backgroundColor: "var(--nibro-sage)", boxShadow: "0 10px 30px -10px rgba(122, 156, 127, 0.6)" }}
        data-testid="ai-chat-toggle"
        aria-label={t("chat.open_aria")}
        aria-expanded={open}
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </button>
    </>
  );
}
